// How the org chart draws the organization.
// Each view can be folded around the selected role: only that role, its
// ancestors and their direct roles are drawn, instead of the whole org.
export enum CirclesGraphViews {
  Circles = 'Circles',
  Tree = 'Tree',
  Members = 'Members',
  // #107: circles side by side, sub-circles around their super-circle
  LinkedCircles = 'LinkedCircles',
  // #107: web of roles that cite each other with #
  RoleLinks = 'RoleLinks',
  // #107: the whole org as a web, each circle wearing its roles
  Network = 'Network',
  // #200: the org's whiteboard (Excalidraw), drawn outside the layout engine.
  // Only in the org chart's menu: not a default view, an export or a share
  // link, which all go through the engine and get circles instead.
  Whiteboard = 'Whiteboard',
}

// Views switched off without removing their code: they leave the menu, and a
// URL, share link or org setting still naming one falls back to the default.
// Tree switched off on 01/10/2026, asked by Yohan before the v3 acceptance
// testing. Remove a view from this list to bring it back.
export const disabledGraphViews: readonly CirclesGraphViews[] = [
  CirclesGraphViews.Tree,
]

export interface GraphView {
  view: CirclesGraphViews
  folded: boolean
  // List the members inside the roles. The members view is made of members, so
  // it is always on there.
  members: boolean
}

export const defaultGraphView: GraphView = {
  view: CirclesGraphViews.Circles,
  folded: false,
  members: true,
}

// Read a view from values coming from a URL, an embed or the database.
// An unknown or disabled view is left to the caller's default. The members
// view is never folded and always shows its members. An instance passes its
// own list of disabled views, the common one plus its own (#208).
export function parseGraphView(
  view: string | null | undefined,
  folded?: boolean | null,
  members?: boolean | null,
  disabled: readonly CirclesGraphViews[] = disabledGraphViews
): GraphView | undefined {
  if (!view || !(view in CirclesGraphViews)) return undefined
  const parsed = view as CirclesGraphViews
  if (disabled.includes(parsed)) return undefined
  const isMembersView = parsed === CirclesGraphViews.Members
  return {
    view: parsed,
    folded: !isMembersView && !!folded,
    members: isMembersView || members !== false,
  }
}
